/** @jsx jsx */
import React from 'react';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import { mixed, object, string } from 'yup';
import axios from 'axios';
import { jsx } from '@emotion/core';
import { withNamespaces } from 'react-i18next';
import Cookies from 'universal-cookie';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import defaultsDeep from 'lodash/defaultsDeep';
import compose from 'lodash/fp/compose';
import { Text, Placeholder } from '@sitecore-jss/sitecore-jss-react';
import { getMonthLabel } from '../../../utils/date';
import { setAntiForgeryToken } from '../../../utils/forms';
import { API, REG_ONLY_NUMBERS } from '../../../utils/constants';
import { FORM_COPY as dict } from '../../../utils/dictionarykeys';
import { SafePureComponent } from '../../../utils/SafePureComponent';
import { showElementCss } from '../../../utils/responsive';
import { modalFadeOutCss, modalFadeInCss } from '../../../utils/animations';
import { setDataLayerObj } from '../../../utils/analytics';
import { withConsumer } from '../../../contexts';
import { modalIds, ModalsConsumer } from '../../../contexts/ModalsContext';
import Modal from '../../ui/Modal';
import DotComponent from '../../ui/DotComponent';
import { Icon } from '../../ui/Icon';
import DatePicker from '../../ui/DatePicker';
import TimePicker from '../../ui/TimePicker';
import { Input, Select, TextArea, Checkbox, PhoneNumber } from '../../ui/Form';
import createButtonCss from '../../ui/Button';
import CtaLink from '../../ui/CtaLink';
import { mapDropdownValues } from '../../ui/Form/Dropdown';
import { Container, Column, Row, MediaQueries } from '../../../styles/Grid';
import ThankYouForm from '../ThankYouForm';
import LottieAnimation, {
  getAmountAnimationIdFromIndex,
} from '../../ui/LottieAnimation';

import * as enquiryStyles from '../Enquiry/index.styles';
import * as styles from './index.styles';

const steps = {
  dateFormScreen: 0,
  infoFormScreen: 1,
  thankYouScreen: 2,
};

const defaultData = {
  AntiForgery: {
    name: '',
    value: '',
  },
  Location: {
    value: '',
  },
  LocationAddress: {
    value: '',
  },
  PrivacyPolicyLink: {
    value: {
      href: '#',
      linktype: 'internal',
      text: 'Link',
      anchor: '',
      querystring: '',
      target: '',
      id: '',
    },
  },
  MoveInDateOptions: [],
  HowManyPeopleOptions: [],
  ForHowLongOptions: [],
  CompanySizeOptions: [],
  PhoneNumberOptions: [
    {
      fields: {
        CountryName: {
          value: '',
        },
        DialingCode: {
          value: '',
        },
        Flag: {
          value: '',
        },
        CountryISOCode3: {
          value: '',
        },
        CountryISOCode2: {
          value: '',
        },
      },
    },
  ],
  DomainCountryISOCode2: {
    value: '',
  },
  LocationID: {
    value: '',
  },
};

const FORM_ID = 'bat_form--contact';

class BookTour extends SafePureComponent {
  phoneNumberRef = React.createRef();
  cookies = new Cookies();
  state = {
    currentStep: steps.dateFormScreen,
    isDropDownOpen: false,
    currentAnimationId: null,
    isNumberOfPeopleSelected: false,
    channelCookie: this.cookies.get('channel_attrib') || 'Organic',
    dateError: null,
    selectedDate: {
      day: '',
      month: '',
      year: '',
    },
    selectedTime: {
      time: '12:00',
      meridiem: 'PM',
    },
  };
  weekends = get(this.props, 'fields.Weekend.value', '06');
  componentDidMount() {
    this.setState({
      currentAnimationId: getAmountAnimationIdFromIndex(0),
    });
  }

  renderHeader(options = {}) {
    const { t } = this.props;
    const { hiddenOnMobile } = options;

    return (
      <div
        css={[
          styles.header,
          hiddenOnMobile && showElementCss(MediaQueries.mdAndUp),
        ]}
      >
        <h1 id="bat_form_headline" css={styles.headline}>
          {t('BookTourHeadline')}
          <DotComponent />
        </h1>
        <div css={styles.subtitleContainer}>
          <div css={styles.phoneContainer}>
            <Placeholder name="jss-phone" rendering={this.props.rendering} />
          </div>
        </div>
      </div>
    );
  }

  changeDate = date => {
    this.setState({ dateError: null, selectedDate: date });
  };

  getStepAnimation = sectionStep =>
    this.state.currentStep !== sectionStep ? modalFadeOutCss : modalFadeInCss;

  renderDateScreen(contextFields) {
    const { t } = this.props;
    const fields = defaultsDeep(this.props.fields, defaultData);
    const { dateError } = this.state;
    const locationText = !isEmpty(fields.Location.value) ? (
      <Text field={fields.Location} editable={false} />
    ) : (
      get(contextFields, 'LocationName', '')
    );

    return (
      <Container css={this.getStepAnimation(steps.dateFormScreen)}>
        <Row>
          <Column cols={{ sm: 6, md: 7, lg: 7 }} offsets={{ lg: 1 }}>
            <div css={styles.screenContainer}>
              <form id="bat_form--choose_date" onSubmit={this.submitDateForm}>
                {this.renderHeader()}
                <h2 css={styles.location}>{locationText}</h2>
                <p css={styles.dateInstruction}>
                  {t('DateAndTimeInstructionText')}
                </p>
                <DatePicker weekends={[...this.weekends]} onChange={this.changeDate} disableWeekends />
                <TimePicker
                  css={styles.timePicker}
                  onChange={time => this.setState({ selectedTime: time })}
                />
                <p css={styles.dateError}>{dateError}</p>
                <div css={styles.submitButtonContainer}>
                  <button css={createButtonCss()}>{t('ContinueCta')}</button>
                </div>
              </form>
            </div>
          </Column>
        </Row>
      </Container>
    );
  }

  renderGeneralError(msg) {
    return (
      <div css={enquiryStyles.generalError}>
        <Icon name="error" customCss={enquiryStyles.errorIcon} />
        {msg}
      </div>
    );
  }

  mapValues(
    values,
    { Location, DomainCountryISOCode2, LocationID },
    contextFields,
  ) {
    const { selectedDate, selectedTime } = this.state;
    const { day, month, year } = selectedDate;
    const { time, meridiem } = selectedTime;
    const countryData = get(
      this.phoneNumberRef,
      'current.state.countryData',
      {},
    );
    const locationName = !isEmpty(Location.value)
      ? Location.value
      : get(contextFields, 'LocationName', '');

    const locationID = !isEmpty(Location.value)
      ? LocationID.value
      : get(contextFields, 'ProtonId', '');

    return {
      ...values,
      ...countryData,
      Location: locationName,
      BookingDate: `${day}-${month}-${year}`,
      BookingTime: `${time} ${meridiem}`,
      ChannelAttrib: this.state.channelCookie,
      DomainCountryISOCode2: DomainCountryISOCode2.value,
      LocationID: locationID.toString(),
    };
  }

  getPhoneParams(values) {
    const countryISOCode2 = get(
      this.phoneNumberRef,
      'current.state.countryData.CountryISOCode2',
      {},
    );
    return `countryISOCode2=${countryISOCode2}&phoneNumber=${values.PhoneNumber}`;
  }

  onNumberOfPeopleDropdownChange = (_event, _value, index) => {
    const placeholderCount = 1;
    const NumberOfPeopleNextState = this.state.isNumberOfPeopleSelected
      ? {
          currentAnimationId: getAmountAnimationIdFromIndex(index),
        }
      : {
          currentAnimationId: getAmountAnimationIdFromIndex(
            index - placeholderCount,
          ),
          isNumberOfPeopleSelected: true,
        };

    this.setState(NumberOfPeopleNextState);
  };

  handleAnalyticsData(data) {
    setDataLayerObj(data);
  }

  handleAnalyticsDataForCurrentStep() {
    const data = {
      formType: 'book a tour',
      activeFormIds:
        this.state.currentStep === steps.dateFormScreen
          ? 'bat_form--choose_date'
          : 'bat_form--contact',
      event: 'modalOpened',
    };

    this.handleAnalyticsData(data);
  }

  renderInfoScreen(contextFields) {
    const { t } = this.props;
    const fields = defaultsDeep(this.props.fields, defaultData);
    const {
      AntiForgery,
      MoveInDateOptions,
      HowManyPeopleOptions,
      ForHowLongOptions,
      CompanySizeOptions,
      PrivacyPolicyLink,
      PhoneNumberOptions,
      DomainCountryISOCode2,
    } = fields;
    const { selectedDate, selectedTime, currentAnimationId } = this.state;
    const { day, month, year } = selectedDate;
    const { time, meridiem } = selectedTime;
    const { Location } = fields;
    const location = !isEmpty(Location.value)
      ? Location.value
      : get(contextFields, 'LocationName', '');
    const renderPrivacyPolicyLinkLabel = () => (
      <React.Fragment>
        <span css={enquiryStyles.privacyLabel}>
          {t('PrivacyPolicyCheckboxAndText')}
        </span>
        <CtaLink cta={PrivacyPolicyLink} />
      </React.Fragment>
    );

    const bookTourSchema = object().shape({
      FullName: string().required(t(dict.REQUIRED_FIELD)),
      Email: string()
        .email(t(dict.INVALID_EMAIL))
        .required(t(dict.REQUIRED_FIELD)),
      PhoneNumber: string()
        .required(t(dict.REQUIRED_FIELD))
        .matches(REG_ONLY_NUMBERS, t(dict.INVALID_PHONE)),
      PrivacyPolicy: mixed().oneOf([true], t(dict.REQUIRED_CHECKBOX)),
    });

    return (
      <Container css={this.getStepAnimation(steps.infoFormScreen)}>
        <Row>
          <Column cols={{ sm: 6, md: 7, lg: 10 }} offsets={{ lg: 1 }}>
            <div css={styles.screenContainer}>
              <button css={styles.backButton} onClick={this.goToDateForm}>
                <Icon customCss={styles.backButtonIcon} name="chevron-left" />
                <span
                  css={[
                    styles.backButtonLabel,
                    showElementCss(MediaQueries.mdAndUp),
                  ]}
                >
                  {t('BackButton')}
                </span>
              </button>
              {this.renderHeader({ hiddenOnMobile: true })}
              <h2 css={styles.location}>{location}</h2>
              <p css={styles.dateInstruction}>
                {t('BookingDateText')}: {day} {t(getMonthLabel(month))} {year},{' '}
                {time} {meridiem}
              </p>
              <Row>
                <Column cols={{ lg: [5, 10] }}>
                  <div css={styles.infoFormContainer}>
                    <Formik
                      initialValues={{
                        FullName: '',
                        Email: '',
                        PhoneNumber: '',
                        PrivacyPolicy: false,
                      }}
                      validationSchema={bookTourSchema}
                      onSubmit={(values, actions) => {
                        axios
                          .post(
                            `${API.PHONE_VALIDATION}${this.getPhoneParams(
                              values,
                            )}`,
                          )
                          .then(() => {
                            axios
                              .post(
                                API.BOOK_A_TOUR,
                                setAntiForgeryToken(
                                  AntiForgery,
                                  this.mapValues(values, fields, contextFields),
                                ),
                              )
                              .then(({ data }) => {
                                this.setState({
                                  currentStep: steps.thankYouScreen,
                                });

                                const {
                                  id,
                                  createDate,
                                  requestedTourDate,
                                } = data;

                                this.handleAnalyticsData({
                                  createDate,
                                  event: 'contactFormResponseObject',
                                  formName: 'book a tour - contact',
                                  formType: 'modal',
                                  id,
                                  requestedTourDate,
                                  solutionType: undefined,
                                  submitButton: 'submit',
                                });
                              })
                              .catch(error => {
                                actions.setStatus({
                                  msg: t(dict.GENERAL_ERROR),
                                });

                                this.handleAnalyticsData({
                                  event: 'contactFormFailedSubmission',
                                  contactFormError: error.message,
                                  formId: FORM_ID,
                                });
                              });
                          })
                          .catch(error => {
                            actions.setErrors({
                              PhoneNumber: t(dict.INVALID_PHONE),
                            });

                            this.handleAnalyticsData({
                              event: 'contactFormFailedSubmission',
                              contactFormError: error.message,
                              formId: FORM_ID,
                            });
                          });
                      }}
                      render={({
                        errors,
                        touched,
                        handleChange,
                        handleSubmit,
                        status,
                        values,
                      }) => (
                        <form id={FORM_ID} onSubmit={handleSubmit}>
                          <Input
                            className="enquiry-input"
                            id="FullName"
                            placeholder={t('FullNamePlaceholder')}
                            onChange={handleChange}
                            error={errors.FullName && touched.FullName}
                            errorMessage={errors.FullName}
                            value={values.FullName}
                          />
                          <Input
                            className="enquiry-input"
                            id="Email"
                            placeholder={t('EmailPlaceholder')}
                            type="email"
                            onChange={handleChange}
                            error={errors.Email && touched.Email}
                            errorMessage={errors.Email}
                            value={values.Email}
                          />

                          <PhoneNumber
                            options={PhoneNumberOptions}
                            className="enquiry-input"
                            id="PhoneNumber"
                            ref={this.phoneNumberRef}
                            defaultValue={DomainCountryISOCode2.value}
                            placeholder={t('PhoneNumberPlaceholder')}
                            onChange={handleChange}
                            error={errors.PhoneNumber && touched.PhoneNumber}
                            errorMessage={errors.PhoneNumber}
                            value={values.PhoneNumber}
                          />

                          <button
                            css={enquiryStyles.tellUsMore}
                            onClick={this.toggleDropDown}
                          >
                            {t('WouldYouLikeToTellUsMore')}
                            <Icon
                              name="chevron-down"
                              customCss={[
                                enquiryStyles.downIcon,
                                this.state.isDropDownOpen &&
                                  enquiryStyles.chevronRotated,
                              ]}
                            />
                          </button>
                          <div
                            css={
                              this.state.isDropDownOpen
                                ? {}
                                : enquiryStyles.dropDown
                            }
                          >
                            {!isEmpty(MoveInDateOptions) && (
                              <Select
                                className="enquiry-input"
                                id="MoveInDate"
                                placeholder={t('MoveInDatePlaceholder')}
                                onChangeProp={handleChange}
                                options={mapDropdownValues(MoveInDateOptions)}
                              />
                            )}
                            <div css={styles.dropdownContainer}>
                              {!isEmpty(HowManyPeopleOptions) && (
                                <Select
                                  className="enquiry-input"
                                  onChangeProp={(...args) => {
                                    this.onNumberOfPeopleDropdownChange(
                                      ...args,
                                    );
                                    handleChange(...args);
                                  }}
                                  id="HowManyPeople"
                                  placeholder={t('HowManyPeoplePlaceholder')}
                                  options={mapDropdownValues(
                                    HowManyPeopleOptions,
                                  )}
                                />
                              )}
                              {!isEmpty(ForHowLongOptions) && (
                                <Select
                                  className="enquiry-input"
                                  onChangeProp={handleChange}
                                  id="ForHowLong"
                                  placeholder={t('ForHowLongPlaceholder')}
                                  options={mapDropdownValues(ForHowLongOptions)}
                                />
                              )}
                            </div>
                            <div css={styles.dropdownContainer}>
                              {!isEmpty(ForHowLongOptions) && (
                                <Input
                                  className="enquiry-input"
                                  id="CompanyName"
                                  onChange={handleChange}
                                  placeholder={t('CompanyNamePlaceholder')}
                                />
                              )}
                              {!isEmpty(CompanySizeOptions) && (
                                <Select
                                  className="enquiry-input"
                                  id="CompanySize"
                                  onChangeProp={handleChange}
                                  placeholder={t('CompanySizePlaceholder')}
                                  options={mapDropdownValues(
                                    CompanySizeOptions,
                                  )}
                                />
                              )}
                            </div>
                            <TextArea
                              className="enquiry-input"
                              onChange={handleChange}
                              id="Comments"
                              rows={3}
                              placeholder={t('CommentsPlaceholder')}
                            />
                          </div>
                          <div css={enquiryStyles.checkboxWrapper}>
                            {status &&
                              status.msg &&
                              this.renderGeneralError(status.msg)}
                            <Checkbox
                              css={[
                                enquiryStyles.privacyCheckbox,
                                enquiryStyles.privacyPolicy,
                              ]}
                              id="PrivacyPolicy"
                              label={renderPrivacyPolicyLinkLabel()}
                              onChangeProp={handleChange}
                              error={
                                errors.PrivacyPolicy && touched.PrivacyPolicy
                              }
                              errorMessage={errors.PrivacyPolicy}
                              value={values.PrivacyPolicy}
                            />
                            <Checkbox
                              id="SendMessages"
                              onChangeProp={handleChange}
                              label={t('CommunicationOfferCheckboxAndText')}
                              value={values.SendMessages}
                            />
                          </div>
                          <div css={styles.submitButtonContainer}>
                            <input
                              type="submit"
                              css={createButtonCss()}
                              value={t('GetInTouchCTA')}
                            />
                          </div>
                        </form>
                      )}
                    />
                  </div>
                </Column>
                <Column
                  css={showElementCss(MediaQueries.lgAndUp)}
                  cols={{ lg: [5, 10] }}
                >
                  <LottieAnimation
                    height={300}
                    nextAnimationId={currentAnimationId}
                  />
                </Column>
              </Row>
            </div>
          </Column>
        </Row>
      </Container>
    );
  }

  renderThankYouScreen(closeModal, contextFields) {
    const { t } = this.props;
    const { selectedDate, selectedTime } = this.state;
    const fields = defaultsDeep(this.props.fields, defaultData);
    const { Location, LocationAddress } = fields;
    const location = !isEmpty(Location.value)
      ? Location.value
      : get(contextFields, 'LocationName', '');
    const address = !isEmpty(LocationAddress.value)
      ? LocationAddress.value
      : get(contextFields, 'LocationAddress', '');

    return (
      <div css={this.getStepAnimation(steps.thankYouScreen)}>
        <ThankYouForm
          {...fields}
          closeModal={closeModal}
          disableCloudinary
          isModal
        >
          <h2 css={styles.location}>{location}</h2>
          <p css={styles.address}>{address}</p>
          <div css={styles.dateSummaryContainer}>
            {Boolean(selectedDate) && (
              <div css={styles.dateSummaryLeftItem}>
                <span css={styles.dateSummaryLabel}>{t('DateText')}</span>
                <span css={styles.dateSummaryValue}>
                  {selectedDate.day} {t(getMonthLabel(selectedDate.month))}
                </span>
              </div>
            )}
            <div css={styles.dateSummaryRightItem}>
              <span css={styles.dateSummaryLabel}>{t('TimeText')}</span>
              <span css={styles.dateSummaryValue}>
                {selectedTime.time} {selectedTime.meridiem}
              </span>
            </div>
          </div>
        </ThankYouForm>
      </div>
    );
  }

  toggleDropDown = e => {
    e.preventDefault();
    this.setState({ isDropDownOpen: !this.state.isDropDownOpen });
  };

  goToDateForm = e => {
    e.preventDefault();
    this.setState({ currentStep: steps.dateFormScreen });
  };

  submitDateForm = e => {
    const { t } = this.props;
    e.preventDefault();

    if (!this.state.selectedDate.day) {
      this.setState({ dateError: t(dict.REQUIRED_DATE) });
      return;
    }

    this.setState({ currentStep: steps.infoFormScreen });

    const bookTourDataLayerObject = {
      formName: 'book a tour - continue',
      formType: 'modal',
      solutionType: undefined,
      submitButton: 'submit',
    };

    this.handleAnalyticsData(bookTourDataLayerObject);
  };

  onAfterOpen = () => {
    this.handleAnalyticsDataForCurrentStep();
  };

  onAfterClose = () => {
    if (this.state.currentStep === steps.thankYouScreen) {
      this.setState({ currentStep: steps.dateFormScreen });
    }
  };

  safeRender() {
    const { modalsContext } = this.props;
    const { currentModal, closeModal, formFields } = modalsContext;

    return (
      <Modal
        isOpen={currentModal === modalIds.bookTour}
        onRequestClose={closeModal}
        onAfterOpen={this.onAfterOpen}
        onAfterClose={this.onAfterClose}
      >
        {this.renderDateScreen(formFields)}
        {this.renderInfoScreen(formFields)}
        {this.renderThankYouScreen(closeModal, formFields)}
      </Modal>
    );
  }
}

const stringPropShape = {
  value: PropTypes.string,
};

BookTour.displayName = 'BookTour';

BookTour.defaultProps = {
  closeModal: () => {},
  fields: defaultData,
  isModal: false,
  t: key => key,
};

BookTour.propTypes = {
  closeModal: PropTypes.func,
  fields: PropTypes.shape({
    AntiForgery: PropTypes.shape({
      name: PropTypes.string,
      value: PropTypes.string,
    }),
    CompanySizeOptions: PropTypes.array,
    DomainCountryISOCode2: PropTypes.shape(stringPropShape),
    ForHowLongOptions: PropTypes.array,
    HowManyPeopleOptions: PropTypes.array,
    Location: PropTypes.shape(stringPropShape),
    LocationAddress: PropTypes.shape(stringPropShape),
    LocationID: PropTypes.shape(stringPropShape),
    MoveInDateOptions: PropTypes.array,
    PhoneNumberOptions: PropTypes.array,
    PrivacyPolicyLink: PropTypes.object,
  }),
  isModal: PropTypes.bool,
  t: PropTypes.func,
};

export default compose(
  withNamespaces(),
  withConsumer(ModalsConsumer, 'modalsContext'),
)(BookTour);
